import React from 'react';
import { css, styled } from '../../../common.styles';
import { paddingInline } from '../../../styles/general/logical-properties';
import { ButtonProps } from '../general/common-button';
import { baseColor, mobile, normalFont, primaryColor, tablet } from '../../../styles/general/theme';
import { DsChevronLeftArrowIcon, DsChevronRightArrowIcon } from '../../icon/ds/common-ds-chevrons';

const ChevronStyle = css<{ disabled?: boolean | undefined }>`
  ${paddingInline(0, 20)};

  ${(p) =>
    p.disabled &&
    css`
      opacity: 0.65;
    `};
`;

const BackChevron = styled(DsChevronLeftArrowIcon)<{ disabled?: boolean | undefined }>`
  ${ChevronStyle}
`;

const NextChevron = styled(DsChevronRightArrowIcon)<{ disabled?: boolean | undefined }>`
  ${ChevronStyle}
`;

const DsArrowAndTextButtonWrapper = styled.a<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  darkMode?: boolean;
}>`
  font-family: ${normalFont};
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  text-align: center;
  color: ${(p) => (p.darkBackground ? baseColor(900) : baseColor(100))};
  display: flex;
  justify-content: space-between;
  cursor: pointer;
  text-decoration: none;
  padding: 4px 20px;
  box-sizing: border-box;
  align-items: center;
  height: 32px;
  min-height: fit-content;
  &:hover {
    color: ${(p) => (p.darkBackground ? primaryColor(500) : baseColor(500))};
  }

  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      color: ${baseColor(560)};
      &:hover {
        color: ${baseColor(560)};
      }
    `};

  ${(p) =>
    p.darkMode &&
    css`
      color: ${baseColor(900)};

      :hover {
        text-decoration: underline;
        color: ${primaryColor(800)};
      }
    `};
`;

export const DsArrowAndTextButton: React.FC<ButtonProps> = (props) => {
  const position = props.iconPosition || 'left';
  return (
    <DsArrowAndTextButtonWrapper {...props}>
      {position === 'left' && <BackChevron />}
      {props.children}
      {position === 'right' && <NextChevron />}
    </DsArrowAndTextButtonWrapper>
  );
};

const BaseStyle = css<{ fullWidth?: boolean }>`
  font-family: ${normalFont};
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px;
  display: flex;
  padding: 4px 20px;
  justify-content: center;
  height: 32px;
  align-items: center;
  border-radius: 24px;
  cursor: pointer;
  pointer-events: auto;
  box-sizing: border-box;
  text-decoration: none;
  min-height: fit-content;
  min-width: max-content;

  ${(p) =>
    p.fullWidth &&
    css`
      width: 100%;
      justify-content: center;
    `};

  @media (max-width: ${tablet}px) {
    justify-content: center;
  }

  @media (max-width: ${mobile}px) {
    justify-content: center;
    width: 100%;
  }
`;

export const DsPrimaryButtonStyle = css<{ darkBackground?: boolean }>`
  ${BaseStyle};
  color: ${(p) => (p.darkBackground ? baseColor(100) : baseColor(900))};
  background-color: ${(p) => (p.darkBackground ? baseColor(900) : baseColor(100))};

  :hover {
    background-color: ${(p) => (p.darkBackground ? primaryColor(800) : primaryColor(500))};
  }
`;

export const DisabledDsStyle = css<{ disabled?: boolean | undefined; darkBackground?: boolean; fullWidth?: boolean }>`
  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      color: ${p.darkBackground ? baseColor(100) : baseColor(400)};
      background-color: ${p.darkBackground ? baseColor(375) : baseColor(860)};

      :hover {
        background-color: ${p.darkBackground ? baseColor(375) : baseColor(860)};
      }
    `};
`;

export const DsPrimaryButton = styled.a<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  darkMode?: boolean;
}>`
  ${DsPrimaryButtonStyle};
  ${DisabledDsStyle};

  ${(p) =>
    p.darkMode &&
    css`
      color: ${baseColor(200)};
      border-color: ${baseColor(900)};
      background-color: ${baseColor(900)};

      :hover {
        background-color: ${primaryColor(800)};
        color: ${baseColor(200)};
      }
    `};
`;

export const DsSecondaryButtonStyle = css<{ darkBackground?: boolean }>`
  ${BaseStyle};
  color: ${(p) => (p.darkBackground ? baseColor(800) : baseColor(100))};
  background-color: ${(p) => (p.darkBackground ? baseColor(100) : baseColor(900))};
  border: 1px solid ${(p) => (p.darkBackground ? baseColor(900) : baseColor(100))};

  :hover {
    background-color: ${(p) => (p.darkBackground ? baseColor(100) : primaryColor(600))};
    border: 1px solid ${(p) => (p.darkBackground ? primaryColor(800) : baseColor(500))};
    color: ${(p) => (p.darkBackground ? primaryColor(800) : primaryColor(500))};
  }
`;

export const DisabledSecondaryDsStyle = css<{ disabled?: boolean | undefined; darkBackground?: boolean }>`
  ${(p) =>
    p.disabled &&
    css`
      cursor: not-allowed;
      background-color: ${p.darkBackground ? baseColor(100) : baseColor(900)};
      border: 1px solid ${p.darkBackground ? baseColor(375) : baseColor(400)};
      color: ${p.darkBackground ? baseColor(375) : baseColor(400)};

      :hover {
        background-color: ${p.darkBackground ? baseColor(100) : baseColor(900)};
        border: 1px solid ${p.darkBackground ? baseColor(375) : baseColor(400)};
        color: ${p.darkBackground ? baseColor(375) : baseColor(400)};
      }
    `};
`;

export const DsSecondaryButton = styled.a<{
  disabled?: boolean | undefined;
  darkBackground?: boolean;
  fullWidth?: boolean;
  darkMode?: boolean;
}>`
  ${DsSecondaryButtonStyle};
  ${DisabledSecondaryDsStyle};

  ${(p) =>
    p.darkMode &&
    css`
      background-color: transparent;
      color: ${baseColor(900)};
      border-color: ${baseColor(900)};

      :hover {
        background-color: transparent;
        border-color: ${primaryColor(800)};
        color: ${primaryColor(800)};
      }
    `};
`;

export const DsTextButton: React.FC<ButtonProps> = (props) => {
  return <DsArrowAndTextButtonWrapper {...props}>{props.children}</DsArrowAndTextButtonWrapper>;
};
export const DsButton = (props: ButtonProps): any => {
  switch (props?.variant?.toLowerCase()) {
    case 'primary':
      return <DsPrimaryButton {...props} />;
    case 'secondary':
      return <DsSecondaryButton {...props} />;
    case 'tertiary':
      return <DsTextButton {...props} />;
    case 'icon':
      return <DsArrowAndTextButton {...props} />;
    default:
      return null;
  }
};
